import { useState } from "react";
import { useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import {
  Avatar,
  Breadcrumb,
  Button,
  Col,
  Input,
  Progress,
  Row,
  Segmented,
  Skeleton,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ApartmentOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  HomeOutlined,
  UnorderedListOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import ReactApexChart from "react-apexcharts";

import { PortalApi } from "../../../services";
import PortalTaskDrawer from "./TaskDrawer";
import { EmptyState, KpiCard, SectionHeader } from "../../../components";
import { useTheme } from "../../../hooks";
import { toFa } from "../../../utils/helpers";
import styles from "../styles.module.css";

const { Title, Text } = Typography;

/* آنچه مشتری از یک پروژه می‌پرسد: چقدر پیش رفته، چه چیزی باز مانده و چه چیزی
 * عقب افتاده. نه ستون‌های کانبان و نه اینکه چه کسی روی چه تسکی است — آن‌ها کار
 * داخلی تیم‌اند و ستون‌های قابل نمایش را هم خودِ تیم در تب «پرتال مشتری» پروژه
 * تعیین می‌کند.
 *
 * پروژه‌ی باز در URL می‌نشیند تا بازگشت مرورگر کار کند و لینک پروژه قابل اشتراک
 * باشد. */

const jalali = (value) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : null;

const Crumbs = ({ title }) => (
  <Breadcrumb
    className="mb-3"
    items={[
      {
        title: (
          <>
            <HomeOutlined />
            <span>پرتال</span>
          </>
        ),
      },
      { title },
    ]}
  />
);

const PageHeader = () => (
  <>
    <Crumbs title="پروژه‌ها" />
    <header className={styles.header}>
      <span>
        <ApartmentOutlined />
      </span>
      <div>
        <Title level={4}>پروژه‌ها</Title>
        <Text>کارهایی که برای شما در جریان است</Text>
      </div>
    </header>
  </>
);

/* سه عددی که کنار هم معنی می‌دهند: چقدر تمام شده، چقدر مانده، و چقدر از موعدش
   گذشته. تنها عددی که رنگ می‌گیرد همان است که باید کاری کرد. */
const MiniStats = ({ project }) => (
  <div className={styles.stats}>
    <span>
      <CheckCircleOutlined />
      {toFa(project.done_tasks || 0)} انجام‌شده
    </span>
    <span>
      <UnorderedListOutlined />
      {toFa(project.open_tasks || 0)} باقی‌مانده
    </span>
    <span className={project.overdue_tasks > 0 ? styles.danger : ""}>
      <WarningOutlined />
      {toFa(project.overdue_tasks || 0)} عقب‌افتاده
    </span>
  </div>
);

const ProjectDetail = ({ uuid, onBack }) => {
  const { isDark, chartColors, chartPalette } = useTheme();
  const [openTask, setOpenTask] = useState(null);

  const { data: project, isFetching } = useQuery({
    queryKey: ["[Portal] Project", uuid],
    queryFn: () =>
      PortalApi.getPortalProject({ params: { uuid } }).then((res) => res.data.project),
  });

  if (isFetching && !project) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (!project) return null;

  const overdue = (task) =>
    !task.is_done && task.due_date && new Date(task.due_date) < new Date();

  return (
    <div className="w-100">
      <Crumbs title={project.title} />

      <div className="ds-panel p-3 mb-3">
        <div className={styles.detailHead}>
          <Tooltip title="بازگشت به پروژه‌ها">
            <Button
              type="text"
              shape="circle"
              icon={<ArrowRightOutlined />}
              aria-label="بازگشت به پروژه‌ها"
              onClick={onBack}
            />
          </Tooltip>
          <Avatar
            size={52}
            shape="square"
            src={project.main_image?.original_url}
            icon={<ApartmentOutlined />}
          />
          <div className={styles.detailText}>
            <Title level={4} className="mb-0">
              {project.title}
            </Title>
            {project.description && <Text>{project.description}</Text>}
            <div className={styles.detailMeta}>
              {jalali(project.due_date) && (
                <span>
                  <ClockCircleOutlined /> مهلت: {jalali(project.due_date)}
                </span>
              )}
              <span>
                <CheckCircleOutlined /> {toFa(project.done_tasks || 0)} از{" "}
                {toFa(project.total_tasks || 0)} کار
              </span>
              {project.overdue_tasks > 0 && (
                <span className={styles.danger}>
                  <WarningOutlined /> {toFa(project.overdue_tasks)} عقب‌افتاده
                </span>
              )}
            </div>
          </div>
          <Progress
            type="circle"
            size={78}
            percent={project.progress}
            format={(value) => `${toFa(value)}٪`}
          />
        </div>
      </div>

      {project.show_stats && (
      <Row gutter={[12, 12]} className="mb-3">
        <Col xs={12} lg={6}>
          <KpiCard
            icon={<UnorderedListOutlined />}
            tone="blue"
            label="کل کارها"
            value={toFa(project.total_tasks || 0)}
          />
        </Col>
        <Col xs={12} lg={6}>
          <KpiCard
            icon={<CheckCircleOutlined />}
            tone="green"
            label="انجام‌شده"
            value={toFa(project.done_tasks || 0)}
          />
        </Col>
        <Col xs={12} lg={6}>
          <KpiCard
            icon={<ClockCircleOutlined />}
            tone="purple"
            label="باقی‌مانده"
            value={toFa(project.open_tasks || 0)}
          />
        </Col>
        <Col xs={12} lg={6}>
          <KpiCard
            icon={<WarningOutlined />}
            tone="orange"
            label="عقب‌افتاده"
            value={toFa(project.overdue_tasks || 0)}
          />
        </Col>
      </Row>
      )}

      {project.show_chart && (
        <div className="ds-panel mb-3">
          <SectionHeader
            icon={<CheckCircleOutlined />}
            tone="green"
            title="روند پیشرفت"
            description="کارهایی که در هشت هفته‌ی گذشته تمام شده‌اند"
          />
          <div className="ds-panel-body">
            {/* پیش از این، نبودِ داده یعنی کل پنل ناپدید می‌شد و آدم فکر می‌کرد
                کلید نمودار کار نکرده. حالا پنل می‌ماند و می‌گوید چرا خالی است. */}
            {(project.trend || []).some((item) => item.done > 0) ? (
            <ReactApexChart
              type="area"
              height={240}
              series={[{ name: "انجام‌شده", data: project.trend.map((item) => item.done) }]}
              options={{
                chart: {
                  background: "transparent",
                  foreColor: chartColors.foreColor,
                  toolbar: { show: false },
                  fontFamily: "IRANYekan",
                  zoom: { enabled: false },
                },
                colors: [chartPalette[0]],
                dataLabels: { enabled: false },
                stroke: { curve: "smooth", width: 3 },
                fill: {
                  type: "gradient",
                  gradient: {
                    opacityFrom: isDark ? 0.38 : 0.28,
                    opacityTo: 0.03,
                    stops: [0, 95, 100],
                  },
                },
                grid: { borderColor: chartColors.gridColor, strokeDashArray: 3 },
                xaxis: {
                  categories: project.trend.map((item) => toFa(item.week)),
                  axisBorder: { show: false },
                },
                yaxis: {
                  min: 0,
                  forceNiceScale: true,
                  labels: { formatter: (value) => toFa(Math.round(value)) },
                },
                tooltip: { theme: isDark ? "dark" : "light" },
              }}
            />
            ) : (
              <EmptyState
                compact
                title="هنوز کاری تمام نشده"
                description="به‌محض اینکه کاری از این پروژه تمام شود، روندش همین‌جا دیده می‌شود."
              />
            )}
          </div>
        </div>
      )}

      {/* مراحل به‌جای یک فهرست بلند، ستون‌های کنار هم‌اند — همان شکلی که کار
          واقعاً در آن جریان دارد. اگر تیم «فهرست کارها» را خاموش کرده باشد،
          سرور اصلاً ستونی نمی‌فرستد و این بخش خالی می‌ماند. */}
      <div className={styles.board}>
        {(project.steps || []).map((step) => (
          <div className={styles.column} key={step.id}>
            <div className={styles.columnHead}>
              <strong>{step.title}</strong>
              <Tag color={step.is_done ? "green" : "default"} bordered={false}>
                {toFa(step.tasks?.length || 0)}
              </Tag>
            </div>

            {step.tasks?.length ? (
              step.tasks.map((task) => (
                <button
                  type="button"
                  className={styles.taskCard}
                  key={task.uuid}
                  onClick={() => setOpenTask(task.uuid)}
                >
                  <span className={task.is_done ? styles.done : ""}>
                    {task.is_done && <CheckCircleOutlined className="ms-1" />}
                    {task.title}
                  </span>
                  {task.due_date && (
                    <Tooltip title="مهلت انجام">
                      <span className={overdue(task) ? styles.danger : styles.meta}>
                        {jalali(task.due_date)}
                      </span>
                    </Tooltip>
                  )}
                </button>
              ))
            ) : (
              <span className={styles.meta}>موردی ندارد</span>
            )}
          </div>
        ))}
      </div>

      <PortalTaskDrawer
        uuid={openTask}
        open={Boolean(openTask)}
        onClose={() => setOpenTask(null)}
      />
    </div>
  );
};

const PortalProjects = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = searchParams.get("project");

  const list = useQuery({
    queryKey: ["[Portal] Projects"],
    queryFn: () => PortalApi.getPortalProjects().then((res) => res.data.projects || []),
    initialData: [],
  });

  if (selected) {
    return <ProjectDetail uuid={selected} onBack={() => setSearchParams({})} />;
  }

  if (list.isFetching && list.data.length === 0) {
    return (
      <div className="w-100">
        <PageHeader />
        <div className={styles.grid}>
          {[1, 2, 3].map((key) => (
            <div className="ds-panel p-3" key={key}>
              <Skeleton active avatar paragraph={{ rows: 2 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (list.data.length === 0) {
    return (
      <div className="w-100">
        <PageHeader />
        <EmptyState
          icon={<ApartmentOutlined />}
          title="پروژه‌ای برای شما باز نشده"
          description="وقتی مجموعه پروژه‌ای را برای شما قابل مشاهده کند، همین‌جا دیده می‌شود."
        />
      </div>
    );
  }

  const search = (searchParams.get("search") || "").trim();
  const scope = searchParams.get("scope") || "all";

  /* فیلتر سمت کلاینت است چون کل فهرست پروژه‌های یک مشتری در همان یک درخواست
     می‌آید و رفت‌وبرگشت دوباره برای چند کارت، هزینه‌ی بی‌دلیلی است. */
  const visible = list.data.filter((project) => {
    if (search && !project.title?.includes(search)) return false;
    if (scope === "open") return project.progress < 100;
    if (scope === "done") return project.progress >= 100;
    return true;
  });

  const setParam = (key, value) => {
    if (!value || value === "all") searchParams.delete(key);
    else searchParams.set(key, value);
    setSearchParams(searchParams);
  };

  return (
    <div className="w-100">
      <PageHeader />

      {/* همان قالب صفحه‌های لیست: نوار جستجو و فیلتر داخل همان کارتی که محتوا
          در آن است، نه شناور روی زمینه. */}
      <div className="w-100 ds-panel pb-3">
        <div className="p-3 border-bottom border-bottom-table">
          <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap-reverse">
            <Input.Search
              allowClear
              enterButton
              className={styles.search}
              placeholder="جستجو در نام پروژه"
              defaultValue={search || undefined}
              onChange={(event) => setParam("search", event.target.value)}
            />
            <Segmented
              value={scope}
              onChange={(value) => setParam("scope", value)}
              options={[
                { label: "همه", value: "all" },
                { label: "در جریان", value: "open" },
                { label: "تکمیل‌شده", value: "done" },
              ]}
            />
          </div>
        </div>

        <div className="p-3">
          {visible.length === 0 ? (
            <EmptyState
              compact
              icon={<ApartmentOutlined />}
              title="پروژه‌ای با این فیلتر نیست"
              description="جستجو یا فیلتر را تغییر دهید."
            />
          ) : (
            <div className={styles.grid}>
        {visible.map((project) => (
          <button
            type="button"
            className={styles.card}
            key={project.uuid}
            onClick={() => setSearchParams({ project: project.uuid })}
          >
            <div className={styles.cardHead}>
              <Avatar
                shape="square"
                size={40}
                src={project.main_image?.original_url}
                icon={<ApartmentOutlined />}
              />
              <div className={styles.cardTitle}>
                <strong>{project.title}</strong>
                {jalali(project.due_date) && (
                  <span className={styles.meta}>مهلت {jalali(project.due_date)}</span>
                )}
              </div>
              <Progress
                type="circle"
                size={44}
                percent={project.progress}
                format={(value) => `${toFa(value)}٪`}
              />
            </div>

            <MiniStats project={project} />

            {project.last_activity && (
              <span className={styles.meta}>
                آخرین به‌روزرسانی: {jalali(project.last_activity)}
              </span>
            )}
          </button>
        ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortalProjects;
